import { Link } from 'react-router-dom'
import BracketBadge from '../results/BracketBadge'
import { trackEvent } from '../../lib/analytics'
import type { SavedDeck } from '../../hooks/useSavedDecks'
import { faceImageURL, type CardImage } from '../../api/cardImages'
import { bandImgArtCrop, halfBandImgArtCrop } from '../../lib/cardArtCrop'
import { useCommonT } from '../../i18n'

/**
 * Fluid-width grid tile for the "Recent decks" band on the returning home. The
 * full DeckCard is too heavy for this band, so this shows the commander art +
 * bracket badge and routes back into the builder for that commander (the
 * hero/revenue path), mirroring the "Continue building" CTA. The `image` is
 * resolved once by the parent grid (a single batched /cards/images call) and
 * passed down.
 *
 * Navigation v2's phone Home reuses the tile with `to` (the deck page, its
 * contract) and a `caption` (the "Bracket N" label). A tile with its own `to`
 * is not the builder funnel, so it does not log `home_continue_build`.
 */
export default function RecentDeckTile({ deck, image, partnerImage, to: toOverride, caption }: {
  deck: SavedDeck
  image?: CardImage
  partnerImage?: CardImage
  to?: string
  caption?: string
}) {
  const t = useCommonT()
  const commander = deck.commander ?? ''
  const to = toOverride
    // eslint-disable-next-line i18next/no-literal-string -- app route
    ?? (commander ? `/build?commander=${encodeURIComponent(commander)}` : '/build')
  const art = image ? faceImageURL(image, { large: false }) : undefined
  const partnerArt = partnerImage ? faceImageURL(partnerImage, { large: false }) : undefined
  const named = deck.name?.trim()
  const label = named || (commander ? (deck.partner ? `${commander} + ${deck.partner}` : commander) : t('untitled_deck'))
  return (
    <Link
      to={to}
      onClick={toOverride ? undefined : () => trackEvent('home_continue_build', { commander: commander || 'unknown', from: 'recent_tile' })}
      className="surface-2 w-full p-3 rounded-xl flex flex-col gap-2 hover:border-gray-600 transition-colors"
    >
      {art ? (
        <div className="relative">
          {/* The wrapper owns radius/border and clips; the crop style zooms
              past the card's side borders (plain object-cover always shows
              the gold frame edges - the 2026-08-15 crop incident). Constants
              validated per aspect in cardArtCrop.ts. */}
          <div className="flex gap-1">
            <span className={`relative overflow-hidden rounded-lg border border-gray-700 h-24 block ${partnerArt ? 'w-1/2' : 'w-full'}`}>
              <img src={art} alt="" loading="lazy" style={partnerArt ? halfBandImgArtCrop : bandImgArtCrop} />
            </span>
            {partnerArt && (
              <span className="relative overflow-hidden rounded-lg border border-gray-700 h-24 block w-1/2">
                <img src={partnerArt} alt="" loading="lazy" style={halfBandImgArtCrop} />
              </span>
            )}
          </div>
          <div className="absolute top-1 left-1">
            <BracketBadge bracket={deck.bracket} size="sm" />
          </div>
        </div>
      ) : (
        <BracketBadge bracket={deck.bracket} size="sm" />
      )}
      <span className="text-sm text-gray-100 font-medium leading-snug line-clamp-2">
        {label}
      </span>
      {caption && <span className="text-xs text-gray-400">{caption}</span>}
    </Link>
  )
}
